const API_BASE = '/api';

// Types
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'USER' | 'ADMIN';
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  balance: number;
  usageLimitUsd: number;
  stripeCustomerId?: string;
}

export interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  key?: string; // Only returned on creation
  permissions: string[];
  lastUsedAt?: string;
  expiresAt?: string;
  isActive: boolean;
  isExpired: boolean;
  createdAt: string;
  gpuContainerId?: string;
  gpuContainerName?: string;
}

export interface Plan {
  id: string;
  name: string;
  price: number;
  priceId: string;
  features: string[];
  limits: {
    gpuHours: number;
    apiCalls: number;
    storage: number;
  };
}

export interface BillingAccount {
  balance: number;
  usageLimitUsd: number;
  currentMonthSpend: number;
  currentMonthEarnings: number;
  totalEarnings: number;
  pendingEarnings: number;
  billingPeriod: string;
  usage: {
    gpuHours: number;
    apiCalls: number;
    storage: number;
    bandwidth: number;
  };
  rates: {
    GPU_COMPUTE: number;
    API_CALLS: number;
    STORAGE: number;
    BANDWIDTH: number;
  };
  fundAmounts: number[];
  stripeConfigured: boolean;
  hasPaymentMethod: boolean;
  paygVerified: boolean;
  paygVerifiedAt?: string;
  autoRechargeEnabled: boolean;
  autoRechargeThreshold: number;
  autoRechargeAmount: number;
  servicesSuspended: boolean;
  servicesSuspendedAt?: string;
}

export interface PaygStatus {
  paygVerified: boolean;
  paygVerifiedAt?: string;
  paygTermsAcceptedAt?: string;
  hasPaymentMethod: boolean;
  autoRechargeEnabled: boolean;
  balance: number;
  servicesSuspended: boolean;
  preAuthActive: boolean;
}

export interface SubscriptionStatus {
  canDeploy: boolean;
  hasPaymentMethod: boolean;
  paygVerified: boolean;
  balance: number;
  autoRechargeEnabled: boolean;
  servicesSuspended: boolean;
  requiredAction: 'add_payment_method' | 'add_funds' | 'setup_payg' | 'enable_auto_recharge' | null;
  message: string | null;
}

export interface AutoRechargeSettings {
  autoRechargeEnabled: boolean;
  autoRechargeThreshold: number;
  autoRechargeAmount: number;
  hasPaymentMethod: boolean;
  servicesSuspended: boolean;
  servicesSuspendedAt?: string;
}

export interface Invoice {
  id: string;
  stripeInvoiceId: string;
  amount: number;
  currency: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'VOID';
  description?: string;
  invoiceUrl?: string;
  pdfUrl?: string;
  periodStart: string;
  periodEnd: string;
  paidAt?: string;
  createdAt: string;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  funding?: string;
}

export interface MonthlyBilling {
  billingPeriod: string;
  label: string;
  isCurrent: boolean;
  isFinal: boolean;
  gpuComputeHours: number;
  gpuComputeCost: number;
  apiCalls: number;
  apiCallsCost: number;
  storageGb: number;
  storageCost: number;
  bandwidthGb: number;
  bandwidthCost: number;
  totalCost: number;
  invoiceId: string | null;
  invoiceStatus: string | null;
}

export interface UsageLimits {
  billingPeriod: string;
  balance: number;
  usageLimitUsd: number;
  usage: {
    gpuHours: number;
    apiCalls: number;
    storage: number;
    bandwidth: number;
  };
  costs: {
    gpuCompute: number;
    apiCalls: number;
    storage: number;
    bandwidth: number;
  };
  totalSpend: number;
  spendPercent: number;
  rates: {
    GPU_COMPUTE: number;
    API_CALLS: number;
    STORAGE: number;
    BANDWIDTH: number;
  };
  warnings: string[];
  withinLimits: boolean;
}

export interface UsageMetric {
  current: number;
  previous: number;
  limit: number;
  costUsd: number;
}

export interface UsageSummary {
  summary: {
    GPU_COMPUTE: UsageMetric;
    API_CALLS: UsageMetric;
    STORAGE: UsageMetric;
    BANDWIDTH: UsageMetric;
  };
  currentMonthTotalCost: number;
  currentMonthEarnings: number;
  totalEarnings: number;
  pendingEarnings: number;
  balance: number;
  billingPeriod: string;
}

export interface MonthlyCostEntry {
  billingPeriod: string;
  gpuComputeHours: number;
  gpuComputeCost: number;
  apiCalls: number;
  apiCallsCost: number;
  storageGb: number;
  storageCost: number;
  bandwidthGb: number;
  bandwidthCost: number;
  totalCostUsd: number;
  isFinal: boolean;
}

// API Error class
export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Fetch wrapper with error handling — session cookie only (no localStorage token).
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(
      response.status,
      data.error || data.message || 'An error occurred',
      data.details
    );
  }

  return data;
}

// Auth API
export const authApi = {
  ssoCallback: async (code: string, codeVerifier: string, state: string) => {
    return fetchApi<{ user: User }>('/auth/sso/callback', {
      method: 'POST',
      body: JSON.stringify({
        code,
        code_verifier: codeVerifier,
        state,
      }),
    });
  },

  logout: async () => {
    await fetchApi('/auth/logout', { method: 'POST' });
  },

  getMe: async () => {
    return fetchApi<{ user: User }>('/auth/me');
  },

  updateProfile: async (data: { firstName?: string; lastName?: string }) => {
    return fetchApi<{ user: User }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  getSessions: async () => {
    return fetchApi<{
      sessions: Array<{
        id: string;
        userAgent?: string;
        ipAddress?: string;
        createdAt: string;
        expiresAt: string;
        isCurrent: boolean;
      }>;
    }>('/auth/sessions');
  },

  revokeSession: async (sessionId: string) => {
    return fetchApi(`/auth/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },

  getNotificationPreferences: async () => {
    return fetchApi<{
      preferences: {
        notifyProductUpdates: boolean;
        notifyUsageAlerts: boolean;
        notifyBilling: boolean;
        notifySecurity: boolean;
      };
    }>('/auth/notifications');
  },

  updateNotificationPreferences: async (preferences: {
    notifyProductUpdates?: boolean;
    notifyUsageAlerts?: boolean;
    notifyBilling?: boolean;
    notifySecurity?: boolean;
  }) => {
    return fetchApi<{
      message: string;
      preferences: {
        notifyProductUpdates: boolean;
        notifyUsageAlerts: boolean;
        notifyBilling: boolean;
        notifySecurity: boolean;
      };
    }>('/auth/notifications', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  },
};

// Billing API (PAYG model)
export const billingApi = {
  getAccount: async () => {
    return fetchApi<BillingAccount>('/billing/account');
  },

  addFunds: async (amount: number) => {
    return fetchApi<{ sessionId: string; url: string }>('/billing/add-funds', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },

  verifyPayment: async (sessionId: string) => {
    return fetchApi<{ message: string; balance: number }>('/billing/verify-payment', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  },

  setUsageLimit: async (limit: number) => {
    return fetchApi<{ message: string; usageLimitUsd: number }>('/billing/set-usage-limit', {
      method: 'POST',
      body: JSON.stringify({ limit }),
    });
  },

  getUsage: async () => {
    return fetchApi<UsageLimits>('/billing/usage');
  },

  getInvoices: async () => {
    return fetchApi<{ invoices: Invoice[] }>('/billing/invoices');
  },

  getPaymentMethod: async () => {
    return fetchApi<{ paymentMethod: PaymentMethod | null }>('/billing/payment-method');
  },

  createSetupIntent: async () => {
    return fetchApi<{ clientSecret: string }>('/billing/setup-intent', {
      method: 'POST',
    });
  },

  updatePaymentMethod: async (paymentMethodId: string) => {
    return fetchApi<{ message: string; paymentMethod: PaymentMethod }>('/billing/payment-method', {
      method: 'PUT',
      body: JSON.stringify({ paymentMethodId }),
    });
  },

  getMonthlyBilling: async () => {
    return fetchApi<{ months: MonthlyBilling[]; rates: Record<string, number> }>('/billing/monthly');
  },

  downloadInvoicePdf: async (invoiceId: string) => {
    const response = await fetch(`${API_BASE}/billing/invoices/${invoiceId}/pdf`, {
      credentials: 'include',
    });
    if (!response.ok) throw new ApiError(response.status, 'Failed to download invoice PDF');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  downloadMonthlyPdf: async (billingPeriod: string) => {
    const response = await fetch(`${API_BASE}/billing/monthly/${billingPeriod}/pdf`, {
      credentials: 'include',
    });
    if (!response.ok) throw new ApiError(response.status, 'Failed to download statement PDF');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  createPortalSession: async () => {
    return fetchApi<{ url: string }>('/billing/create-portal-session', {
      method: 'POST',
    });
  },

  getAutoRecharge: async () => {
    return fetchApi<AutoRechargeSettings>('/billing/auto-recharge');
  },

  setAutoRecharge: async (config: { enabled: boolean; threshold?: number; amount?: number }) => {
    return fetchApi<{
      message: string;
      autoRechargeEnabled: boolean;
      autoRechargeThreshold: number;
      autoRechargeAmount: number;
      balance: number;
      servicesSuspended: boolean;
    }>('/billing/auto-recharge', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },

  resumeServices: async () => {
    return fetchApi<{ message: string; servicesSuspended: boolean }>('/billing/resume-services', {
      method: 'POST',
    });
  },

  getPaygStatus: async () => {
    return fetchApi<PaygStatus>('/billing/payg-status');
  },

  acceptPaygTerms: async () => {
    return fetchApi<{ message: string; paygTermsAcceptedAt: string }>('/billing/accept-payg-terms', {
      method: 'POST',
    });
  },

  getSubscriptionStatus: async () => {
    return fetchApi<SubscriptionStatus>('/billing/subscription-status');
  },

  getRates: async () => {
    return fetchApi<{
      rates: Record<string, number>;
      fundAmounts: number[];
      platformFeePercent: number;
      gstPercent: number;
    }>('/billing/rates');
  },

  getActiveResources: async () => {
    return fetchApi<{
      deployments: Array<{
        type: 'DEPLOYMENT';
        id: string;
        name: string;
        workspaceName: string;
        gpuType: string | null;
        ratePerHour: number;
        ratePerSecond: number;
        totalCostUsd: number;
        elapsedSeconds: number;
        meteringStartedAt: string | null;
      }>;
      rentals: Array<{
        type: 'GPU_RENTAL';
        id: string;
        name: string;
        mode: string;
        ratePerHour: number;
        ratePerSecond: number;
        totalCostUsd: number;
        hoursUsed: number;
        prepaidHours: number | null;
        elapsedSeconds: number;
        startedAt: string;
      }>;
      summary: {
        totalActiveResources: number;
        totalBurnPerHour: number;
        totalBurnPerSecond: number;
        balance: number;
        estimatedRunwaySeconds: number | null;
        estimatedRunwayHours: number | null;
      };
    }>('/billing/active-resources');
  },
};

// Seller API
export const sellerApi = {
  getProfile: async () => {
    return fetchApi<any>('/seller/profile');
  },

  onboard: async () => {
    return fetchApi<{ url: string }>('/seller/onboard', { method: 'POST' });
  },

  completeOnboarding: async () => {
    return fetchApi<{ complete: boolean; bankVerified: boolean; message: string }>('/seller/onboard/complete', { method: 'POST' });
  },

  getEarningsHistory: async (months?: number) => {
    return fetchApi<{
      history: Array<{
        billingPeriod: string;
        grossRevenue: number;
        platformFee: number;
        netEarnings: number;
        transactionCount: number;
      }>;
    }>(`/seller/earnings/history${months ? `?months=${months}` : ''}`);
  },
};

// GPU Container interface
export interface GpuContainer {
  id: string;
  name: string;
  status: string;
  runningTasksCount: number;
  pendingTasksCount: number;
  registeredContainerInstancesCount: number;
  activeServicesCount: number;
  tags?: Record<string, string>;
}

// API Keys API
export const apiKeysApi = {
  list: async () => {
    return fetchApi<{ apiKeys: ApiKey[] }>('/keys');
  },

  // Fetch available GPU containers from infrastructure
  getGpuContainers: async () => {
    return fetchApi<{ gpuContainers: GpuContainer[]; error?: string }>('/keys/gpu-containers');
  },

  create: async (data: {
    name: string;
    expiresIn?: '30d' | '90d' | '1y' | 'never';
    permissions?: string[];
    gpuContainerId?: string;
    gpuContainerName?: string;
  }) => {
    return fetchApi<{ apiKey: ApiKey & { syncedToInfrastructure?: boolean } }>('/keys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  revoke: async (id: string) => {
    return fetchApi(`/keys/${id}`, {
      method: 'DELETE',
    });
  },

  rotate: async (id: string) => {
    return fetchApi<{ apiKey: ApiKey }>(`/keys/${id}/rotate`, {
      method: 'POST',
    });
  },

  getInfrastructureKey: async () => {
    return fetchApi<{ apiKey: { id: string; key?: string; prefix?: string; masked?: string } }>('/keys/infrastructure');
  },
};

// Usage API
export const usageApi = {
  getSummary: async () => {
    return fetchApi<UsageSummary>('/usage/summary');
  },

  getHistory: async (days: number) => {
    return fetchApi<{ history: Array<{ date: string; GPU_COMPUTE: number; API_CALLS: number; STORAGE: number; BANDWIDTH: number; costUsd: number }> }>(`/usage/history?days=${days}`);
  },

  getCostHistory: async () => {
    return fetchApi<{ costHistory: MonthlyCostEntry[] }>('/usage/cost-history');
  },
};

// ============================================================
// Admin Audit Types
// ============================================================

export interface AuditLogEntry {
  id: string;
  userId?: string;
  action: string;
  resource?: string;
  resourceId?: string;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  createdAt: string;
}

export interface SecurityAlert {
  id: string;
  type: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  title: string;
  description: string;
  userId?: string;
  ipAddress?: string;
  metadata?: Record<string, unknown>;
  status: 'OPEN' | 'ACKNOWLEDGED' | 'INVESTIGATING' | 'RESOLVED' | 'FALSE_POSITIVE';
  resolvedBy?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface AuditLogStats {
  totalEvents: number;
  criticalEvents: number;
  warningEvents: number;
  infoEvents: number;
  uniqueUsers: number;
  actionBreakdown: Array<{ action: string; count: number }>;
  hourlyBreakdown: Array<{ hour: string; count: number }>;
}

export interface SecurityAlertSummary {
  openAlerts: number;
  criticalAlerts: number;
  totalAlerts: number;
  alertsByType: Array<{ type: string; count: number }>;
  alertsBySeverity: Array<{ severity: string; count: number }>;
}

export interface AnomalySummary {
  failedLogins: { total: number; uniqueIPs: number; uniqueEmails: number };
  apiAnomalies: { total: number; affectedUsers: number };
  privilegeEscalations: { total: number; uniqueUsers: number };
  topOffendingIPs: Array<{ ip: string; count: number }>;
  topTargetedAccounts: Array<{ email: string; count: number }>;
}

export interface LoggingConfig {
  cloudwatch: { enabled: boolean; logGroup?: string; region?: string };
  datadog: { enabled: boolean; site?: string };
  elk: { enabled: boolean; url?: string; indexPrefix?: string };
  webhook: { enabled: boolean; url?: string };
}

// ============================================================
// Admin Audit API
// ============================================================

export const adminApi = {
  // Audit Logs
  getAuditLogs: async (params?: {
    page?: number;
    limit?: number;
    action?: string;
    userId?: string;
    severity?: string;
    resource?: string;
    ipAddress?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.set(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return fetchApi<{ auditLogs: AuditLogEntry[]; pagination: Pagination }>(
      `/admin/audit-logs${query ? `?${query}` : ''}`
    );
  },

  getAuditLogStats: async (hours: number = 24) => {
    return fetchApi<{ stats: AuditLogStats }>(`/admin/audit-logs/stats?hours=${hours}`);
  },

  getAuditLogActions: async () => {
    return fetchApi<{ actions: string[] }>('/admin/audit-logs/actions');
  },

  // Security Alerts
  getSecurityAlerts: async (params?: {
    page?: number;
    limit?: number;
    type?: string;
    severity?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.set(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return fetchApi<{ alerts: SecurityAlert[]; pagination: Pagination }>(
      `/admin/security-alerts${query ? `?${query}` : ''}`
    );
  },

  getSecurityAlertSummary: async (hours: number = 24) => {
    return fetchApi<{ summary: SecurityAlertSummary }>(`/admin/security-alerts/summary?hours=${hours}`);
  },

  updateAlertStatus: async (id: string, status: string) => {
    return fetchApi<{ alert: SecurityAlert }>(`/admin/security-alerts/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  // Anomaly Detection
  getAnomalySummary: async (hours: number = 24) => {
    return fetchApi<{ summary: AnomalySummary }>(`/admin/anomaly-detection/summary?hours=${hours}`);
  },

  // Logging Config
  getLoggingConfig: async () => {
    return fetchApi<{ config: LoggingConfig }>('/admin/logging-config');
  },
};

// ─── GPU Agent Types ─────────────────────────────────────

export interface GpuAgentHardware {
  gpus: Array<{
    vendor: string;
    model: string;
    vram: number;
    vramUsed?: number;
    temperatureGpu?: number;
    utilizationGpu?: number;
  }>;
  cpu: {
    manufacturer: string;
    brand: string;
    cores: number;
    physicalCores: number;
    speed: number;
    speedMax?: number;
    usage?: number;
  };
  memory: {
    total: number;
    used: number;
    free: number;
  };
  disk?: {
    total: number;
    used: number;
    free: number;
  };
}

export interface GpuAgentResourceLock {
  locked: boolean;
  gpuPercent: number;
  cpuCores: number;
  ramGb: number;
}

export interface GpuAgent {
  id: string;
  userId: string;
  name: string;
  agentUrl: string;
  status: 'PENDING' | 'ONLINE' | 'OFFLINE' | 'ERROR';
  lastSeenAt?: string;
  hardwareInfo?: GpuAgentHardware;
  resourceLock?: GpuAgentResourceLock;
  reliabilityInfo?: {
    overallState: string;
    scores: Record<string, number>;
    recommendation: string;
  };
  allowSelfSigned: boolean;
  createdAt: string;
  updatedAt: string;
  listing?: AgentListing | null;
}

export interface ComputeTask {
  id: string;
  command: string;
  args: string[];
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  submittedAt: number;
  startedAt: number | null;
  completedAt: number | null;
  stdout: string;
  stderr: string;
  exitCode: number | null;
  resourceUsage: Record<string, unknown> | null;
  timeout: number;
}

// ─── GPU Agent API ───────────────────────────────────────

export const agentApi = {
  register: async (data: { name: string; agentUrl: string; apiKey: string; allowSelfSigned?: boolean }) => {
    return fetchApi<{ agent: GpuAgent }>('/agents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  generatePairingCode: async () => {
    return fetchApi<{ code: string; expiresAt: number }>('/agents/pair/generate', {
      method: 'POST',
    });
  },

  claimPairingCode: async (data: {
    code: string;
    name: string;
    apiKey: string;
    hardware?: any;
    resources?: any;
    reliability?: any;
  }) => {
    return fetchApi<{ agent: GpuAgent }>('/agents/pair/claim', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  list: async () => {
    return fetchApi<{ agents: GpuAgent[] }>('/agents');
  },

  get: async (id: string, refresh?: boolean) => {
    return fetchApi<{ agent: GpuAgent; warning?: string }>(
      `/agents/${id}${refresh ? '?refresh=true' : ''}`
    );
  },

  remove: async (id: string) => {
    return fetchApi<{ message: string }>(`/agents/${id}`, {
      method: 'DELETE',
    });
  },

  verify: async (id: string) => {
    return fetchApi<{ agent: GpuAgent; verified: boolean }>(`/agents/${id}/verify`, {
      method: 'POST',
    });
  },

  submitCompute: async (
    id: string,
    task: { command: string; args?: string[]; env?: Record<string, string>; timeout?: number }
  ) => {
    return fetchApi<{ task: ComputeTask }>(`/agents/${id}/compute`, {
      method: 'POST',
      body: JSON.stringify(task),
    });
  },

  listTasks: async (id: string) => {
    return fetchApi<{ tasks: ComputeTask[] }>(`/agents/${id}/compute`);
  },

  getTask: async (id: string, taskId: string) => {
    return fetchApi<{ task: ComputeTask }>(`/agents/${id}/compute/${taskId}`);
  },

  cancelTask: async (id: string, taskId: string) => {
    return fetchApi<{ status: string; id: string }>(`/agents/${id}/compute/${taskId}`, {
      method: 'DELETE',
    });
  },

  createListing: async (
    id: string,
    data: { title: string; description?: string; pricePerHour: number; region: string; country: string; tags?: string[] }
  ) => {
    return fetchApi<{ listing: AgentListing }>(`/agents/${id}/list`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateListing: async (id: string, data: Partial<{ title: string; description: string; pricePerHour: number; region: string; country: string; tags: string[] }>) => {
    return fetchApi<{ listing: AgentListing }>(`/agents/${id}/list`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  removeListing: async (id: string) => {
    return fetchApi<{ message: string }>(`/agents/${id}/list`, {
      method: 'DELETE',
    });
  },

  getListing: async (id: string) => {
    return fetchApi<{ listing: AgentListing | null }>(`/agents/${id}/list`);
  },
};

// ─── Agent Listing type ─────────────────────────────────

export interface AgentListing {
  id: string;
  title: string;
  pricePerHour: number;
  region: string;
  country: string;
  isActive: boolean;
  avgRating: number;
  reviewCount: number;
  totalJobs: number;
}

// ─── Marketplace types ──────────────────────────────────

export interface MarketplaceSeller {
  id: string;
  displayName: string;
  memberSince: string;
}

export interface MarketplaceListing {
  id: string;
  title: string;
  description: string;
  pricePerHour: number;
  region: string;
  country: string;
  tags: string[];
  avgRating: number;
  reviewCount: number;
  totalJobs: number;
  isOccupied: boolean;
  createdAt: string;
  agent: {
    id: string;
    name: string;
    status: 'PENDING' | 'ONLINE' | 'OFFLINE' | 'ERROR';
    lastSeenAt?: string;
    hardwareInfo?: GpuAgentHardware;
    resourceLock?: GpuAgentResourceLock;
    reliabilityInfo?: {
      overallState: string;
      scores: Record<string, number>;
      recommendation: string;
    };
  };
  seller: MarketplaceSeller;
}

export interface ListingReview {
  id: string;
  rating: number;
  comment: string;
  createdAt: string;
  reviewer: {
    id: string;
    displayName: string;
  };
}

export interface MarketplaceListingDetail extends MarketplaceListing {
  isActive: boolean;
  isOccupied: boolean;
  reviews: ListingReview[];
  userHasReviewed: boolean;
}

export interface MarketplaceFilters {
  regions: string[];
  countries: string[];
  gpuModels: string[];
  priceRange: { min: number; max: number };
  vramRange: { min: number; max: number };
}

export interface MarketplacePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface MarketplaceSearchParams {
  search?: string;
  region?: string;
  country?: string;
  gpuModel?: string;
  minVram?: string;
  maxVram?: string;
  minPrice?: string;
  maxPrice?: string;
  minRating?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: string;
  page?: string;
  limit?: string;
}

// ─── Marketplace API ────────────────────────────────────

export const marketplaceApi = {
  list: async (params: MarketplaceSearchParams = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') query.set(k, v);
    });
    const qs = query.toString();
    return fetchApi<{ listings: MarketplaceListing[]; pagination: MarketplacePagination }>(
      `/marketplace${qs ? `?${qs}` : ''}`
    );
  },

  getFilters: async () => {
    return fetchApi<MarketplaceFilters>('/marketplace/filters');
  },

  get: async (id: string) => {
    return fetchApi<{ listing: MarketplaceListingDetail }>(`/marketplace/${id}`);
  },

  submitReview: async (id: string, data: { rating: number; comment?: string }) => {
    return fetchApi<{ review: ListingReview }>(`/marketplace/${id}/reviews`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getReviews: async (id: string, page = 1, limit = 20) => {
    return fetchApi<{ reviews: ListingReview[]; pagination: MarketplacePagination }>(
      `/marketplace/${id}/reviews?page=${page}&limit=${limit}`
    );
  },

  updateReview: async (id: string, data: { rating: number; comment?: string }) => {
    return fetchApi<{ review: ListingReview }>(`/marketplace/${id}/reviews`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteReview: async (id: string) => {
    return fetchApi<{ message: string }>(`/marketplace/${id}/reviews`, {
      method: 'DELETE',
    });
  },

  rentGpu: async (id: string, data: { mode: 'open_ended' | 'prepaid_hours'; hours?: number }) => {
    return fetchApi<GpuRentalResponse>(`/marketplace/${id}/rent`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getRentals: async (status?: string) => {
    const qs = status ? `?status=${status}` : '';
    return fetchApi<{ rentals: GpuRental[] }>(`/marketplace/rentals${qs}`);
  },

  getRental: async (rentalId: string) => {
    return fetchApi<{ rental: GpuRentalDetail }>(`/marketplace/rentals/${rentalId}`);
  },

  releaseRental: async (rentalId: string) => {
    return fetchApi<{ rental: { id: string; status: string; hoursUsed: number; totalCostUsd: number; endedAt: string } }>(
      `/marketplace/rentals/${rentalId}/release`,
      { method: 'POST' }
    );
  },
};

// ─── GPU Rental types ───────────────────────────────────

export interface GpuRental {
  id: string;
  listingId: string;
  listingTitle: string;
  mode: 'OPEN_ENDED' | 'PREPAID_HOURS';
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
  pricePerHour: number;
  listingPricePerHour: number;
  discountPercent: number;
  prepaidHours: number | null;
  hoursUsed: number;
  totalCostUsd: number;
  sellerEarningsUsd: number;
  platformFeeUsd: number;
  startedAt: string;
  endedAt: string | null;
  listing: {
    id: string;
    title: string;
    pricePerHour: number;
    region: string;
    country: string;
    agent: {
      id: string;
      name: string;
      status: string;
      hardwareInfo?: GpuAgentHardware;
    };
  };
  apiKeyPrefix: string | null;
}

export interface GpuRentalDetail extends GpuRental {
  computeEndpoint: string;
}

export interface GpuRentalResponse {
  rental: {
    id: string;
    listingId: string;
    listingTitle: string;
    mode: string;
    status: string;
    pricePerHour: number;
    listingPricePerHour: number;
    discountPercent: number;
    prepaidHours: number | null;
    hoursUsed: number;
    totalCostUsd: number;
    sellerEarningsUsd: number;
    platformFeeUsd: number;
    gstPercent: number;
    gstAmount: number;
    totalBuyerCost: number;
    platformFeePercent: number;
    startedAt: string;
  };
  apiKey: {
    id: string;
    key: string;
    keyPrefix: string;
  };
  computeEndpoint: string;
}